/**
 * Production-Safe Logger
 *
 * Wraps console methods to automatically disable logging in production.
 * Provides structured logging with prefixes for better organization.
 *
 * Usage:
 *   import { logger } from '@/lib/platform/utils/logger';
 *
 *   logger.log('User logged in', { userId: '123' });
 *   logger.error('Failed to save', error);
 *   logger.warn('Deprecated API used');
 *   logger.info('Cache hit', { key: 'user:123' });
 *   logger.debug('State updated', { newState });
 */

type LogLevel = 'log' | 'error' | 'warn' | 'info' | 'debug';

interface LoggerConfig {
  enabled: boolean;
  minLevel: LogLevel;
  prefix?: string;
}

class Logger {
  private config: LoggerConfig;
  private isDevelopment: boolean;

  constructor(config?: Partial<LoggerConfig>) {
    this.isDevelopment = process.env.NODE_ENV !== 'production';

    this.config = {
      enabled: this.isDevelopment,
      minLevel: 'log',
      prefix: '',
      ...config,
    };
  }

  /**
   * Create a child logger with a prefix
   * Useful for namespacing logs by module
   *
   * @example
   * const log = logger.child('[Auth]');
   * log.info('User logged in');  // Output: [Auth] User logged in
   */
  child(prefix: string): Logger {
    return new Logger({
      ...this.config,
      prefix: this.config.prefix ? `${this.config.prefix} ${prefix}` : prefix,
    });
  }

  /**
   * Standard log output
   */
  log(message: string, ...args: unknown[]): void {
    if (!this.config.enabled) return;
    const prefix = this.config.prefix || '';
    console.log(prefix ? `${prefix} ${message}` : message, ...args);
  }

  /**
   * Error logging (always shown, even in production)
   */
  error(message: string, ...args: unknown[]): void {
    const prefix = this.config.prefix || '';
    console.error(prefix ? `${prefix} ${message}` : message, ...args);
  }

  /**
   * Warning logging
   */
  warn(message: string, ...args: unknown[]): void {
    if (!this.config.enabled) return;
    const prefix = this.config.prefix || '';
    console.warn(prefix ? `${prefix} ${message}` : message, ...args);
  }

  /**
   * Info logging
   */
  info(message: string, ...args: unknown[]): void {
    if (!this.config.enabled) return;
    const prefix = this.config.prefix || '';
    console.info(prefix ? `${prefix} ${message}` : message, ...args);
  }

  /**
   * Debug logging
   */
  debug(message: string, ...args: unknown[]): void {
    if (!this.config.enabled) return;
    const prefix = this.config.prefix || '';
    console.debug(prefix ? `${prefix} ${message}` : message, ...args);
  }

  /**
   * Performance timing
   */
  time(label: string): void {
    if (!this.config.enabled) return;
    const prefix = this.config.prefix || '';
    console.time(prefix ? `${prefix} ${label}` : label);
  }

  timeEnd(label: string): void {
    if (!this.config.enabled) return;
    const prefix = this.config.prefix || '';
    console.timeEnd(prefix ? `${prefix} ${label}` : label);
  }

  /**
   * Group logging
   */
  group(label: string): void {
    if (!this.config.enabled) return;
    const prefix = this.config.prefix || '';
    console.group(prefix ? `${prefix} ${label}` : label);
  }

  groupEnd(): void {
    if (!this.config.enabled) return;
    console.groupEnd();
  }

  /**
   * Table output
   */
  table(data: Record<string, unknown>[] | Record<string, unknown>): void {
    if (!this.config.enabled) return;
    console.table(data);
  }
}

/**
 * Default logger instance
 */
export const logger = new Logger();

/**
 * Pre-configured loggers for common modules
 */
export const apiLogger = logger.child('[API]');
export const dbLogger = logger.child('[DB]');
export const authLogger = logger.child('[Auth]');
export const pluginLogger = logger.child('[Plugin]');
export const cacheLogger = logger.child('[Cache]');
export const rlsLogger = logger.child('[RLS]');
export const auditLogger = logger.child('[Audit]');
export const emailLogger = logger.child('[Email]');
